import React from 'react';
import { StackShim as Stack } from '@fluentui/react-migration-v8-v9';
import { IColumn, ColumnActionsMode, IDetailsColumnStyles } from '@fluentui/react';
import { Persona, PresenceBadgeStatus, Tooltip, makeStyles } from '@fluentui/react-components';
import { Link } from 'react-router-dom';
import moment from 'moment';
import { IDividendsReviewSearchResult } from './IDividendsReviewSearchResults';
import StatDateFormats from '../../../../utils/types/DateFormats';
import momentUtc from '../../../../utils/DateFormatter';
import formatCurrency from '../../../../utils/CurrencyFormatter';
import ReviewStatus from '../../../reviews/ReviewStatus';

const useStyles = makeStyles({
  dividendAmounts: {
    display: 'block',
    textAlign: 'right',
    padding: 0,
  },
  approvalPersona: {
    flexBasis: '175px',
    width: '250px',
  },
});

const onRenderCompanyCode = (item: IDividendsReviewSearchResult): JSX.Element => {
  return (
    <Tooltip withArrow content={<span>{item.processName}</span>} relationship="description">
      <Link to={`/companies/${item.companyCode}/dividends-review/${item.id}`}>
        {item.companyCode}-{item.companyName}
      </Link>
    </Tooltip>
  );
};

const onRenderFilingDueDate = (item: IDividendsReviewSearchResult): JSX.Element => {
  return <span>{`${momentUtc(item.filingDueDate, StatDateFormats.DayBreakout)}`}</span>;
};

const onRenderReportingPeriodEndDate = (item: IDividendsReviewSearchResult): JSX.Element => {
  return <span>{`${momentUtc(item.reportingPeriodEndDate, StatDateFormats.DayBreakout)}`}</span>;
};

const onRenderSubmissionDate = (item: IDividendsReviewSearchResult): JSX.Element => {
  const today = moment().utc().startOf('day');
  const days = today.diff(moment(item.submissionDate).utc().startOf('day'), 'days');
  const string = days === 1 ? ' day old' : ' days old';
  return (
    <>
      <span>{`${momentUtc(item.submissionDate, StatDateFormats.DayBreakout)}`}</span>
      <br />
      <span> {`${days}${string}`}</span>
    </>
  );
};

const getStatusStyle = (item: number): PresenceBadgeStatus | undefined => {
  if (item === ReviewStatus.Accepted) {
    return 'available';
  }
  if (item === ReviewStatus.Rejected) {
    return 'blocked';
  }
  if (item === ReviewStatus.ChangesRequested) {
    return 'away';
  }

  return undefined;
};

const onRenderStatus = (item: IDividendsReviewSearchResult): JSX.Element => {
  return <span>{item.reviewStatus}</span>;
};

const DividendsCell = ({ item }: { item: IDividendsReviewSearchResult }): JSX.Element => {
  const styles = useStyles();

  const proposed = item.currencyCode
    ? formatCurrency(item.currencyCode, item.proposedDividend)
    : '--';
  const max = item.currencyCode ? formatCurrency(item.currencyCode, item.maxDividend) : '--';

  return (
    <span className={styles.dividendAmounts}>
      <strong>Proposed: </strong> <span>{proposed}</span>
      <br />
      <strong>Max: </strong> <span>{max}</span>
    </span>
  );
};

const onRenderDividends = (item: IDividendsReviewSearchResult): JSX.Element => {
  return <DividendsCell item={item} />;
};

const rightAlignStyle: Partial<IDetailsColumnStyles> = {
  cellTitle: {
    justifyContent: 'flex-end',
    textAlign: 'right',
  },
};

const ApprovalsCell = ({ item }: { item: IDividendsReviewSearchResult }): JSX.Element => {
  const styles = useStyles();

  return (
    <Stack horizontal>
      <Persona
        name="Tax Dividend"
        avatar={{
          size: 28,
          color: 'colorful',
          badge: getStatusStyle(item.taxStatusId)
            ? { status: getStatusStyle(item.taxStatusId) as PresenceBadgeStatus }
            : undefined,
        }}
        size="small"
        className={styles.approvalPersona}
      />
      <Persona
        name="Treasury"
        avatar={{
          size: 28,
          color: 'colorful',
          badge: getStatusStyle(item.treasuryStatusId)
            ? { status: getStatusStyle(item.treasuryStatusId) as PresenceBadgeStatus }
            : undefined,
        }}
        size="small"
        className={styles.approvalPersona}
      />
    </Stack>
  );
};

const onRenderApprovals = (item: IDividendsReviewSearchResult): JSX.Element => {
  return <ApprovalsCell item={item} />;
};

const getColumns = (): Array<IColumn> => {
  return [
    {
      key: 'companyCode',
      name: 'Company',
      columnActionsMode: ColumnActionsMode.disabled,
      minWidth: 150,
      maxWidth: 150,
      onRender: onRenderCompanyCode,
    },
    {
      key: 'reportingPeriodEndDate',
      name: 'Reporting Period End Date',
      fieldName: 'reportingPeriodEndDate',
      minWidth: 150,
      maxWidth: 180,
      isCollapsible: true,
      columnActionsMode: ColumnActionsMode.disabled,
      onRender: onRenderReportingPeriodEndDate,
    },
    {
      key: 'filingDueDate',
      name: 'Filing Due Date',
      minWidth: 100,
      maxWidth: 140,
      columnActionsMode: ColumnActionsMode.disabled,
      onRender: onRenderFilingDueDate,
    },
    {
      key: 'submissionDate',
      name: 'Submission Date',
      fieldName: 'submissionDate',
      minWidth: 130,
      maxWidth: 130,
      columnActionsMode: ColumnActionsMode.disabled,
      onRender: onRenderSubmissionDate,
    },
    {
      key: 'dividendAmounts',
      name: 'Dividend Amounts',
      minWidth: 200,
      maxWidth: 250,
      columnActionsMode: ColumnActionsMode.disabled,
      onRender: onRenderDividends,
      styles: rightAlignStyle,
    },
    {
      key: 'approvals',
      name: 'Approvals',
      columnActionsMode: ColumnActionsMode.disabled,
      minWidth: 250,
      maxWidth: 275,
      onRender: onRenderApprovals,
    },
    {
      key: 'reviewStatus',
      name: 'Status',
      columnActionsMode: ColumnActionsMode.disabled,
      minWidth: 100,
      maxWidth: 150,
      onRender: onRenderStatus,
      isCollapsible: true,
    },
  ];
};

export default getColumns;
